import { useState } from 'react';
import { Controller, SubmitHandler, useForm } from 'react-hook-form';

import { datadogRum } from '@datadog/browser-rum';
import { Box, Button, FormLabel, Stack, TextField, Typography } from '@mui/material';

import { useSageAuth } from 'src/auth/useSageAuth';
import DetailsPageHeader from 'src/components/fragments/DetailsPageHeader';
import {
    GetIdentityProviderConnectionsApiResponse,
    useLazyGetIdentityProviderConnectionsQuery,
} from 'src/store/sageIdentityProviderSyncApi';
import { snackActions } from 'src/utils/snackBarTools';

interface SSOLoginProps {
    onBack: () => void;
}

type FormValues = {
    email: string;
};

export default function SSOLogin(props: SSOLoginProps) {
    const { onBack } = props;

    const { handleSubmit, control, formState } = useForm<FormValues>({
        mode: 'onChange',
        defaultValues: { email: '' },
    });

    const { login } = useSageAuth();

    const [isLoading, setIsLoading] = useState(false);
    const [getIdentityProviderConnections] = useLazyGetIdentityProviderConnectionsQuery();

    const onSubmit: SubmitHandler<FormValues> = async (data: FormValues) => {
        const domain = data.email.split('@').pop();
        if (!domain) {
            snackActions.error('Invalid email domain.');
            return;
        }
        setIsLoading(true);
        let response: GetIdentityProviderConnectionsApiResponse;
        try {
            response = await getIdentityProviderConnections({ domain }).unwrap();
        } catch (error: any) {
            if (error?.status === 404) {
                snackActions.error('Email is not associated with a Single Sign-On organization.');
            } else {
                snackActions.error('Unable to find organization.');
            }
            setIsLoading(false);
            return;
        }
        try {
            await login({
                connection: response.auth0ConnectionName,
                loginHint: data.email,
                forceLogin: true,
            });
        } catch (loginError) {
            datadogRum.addError(loginError, { context: 'ssoLogin' });
            snackActions.error('Single Sign-On did not complete. Please try again.');
            setIsLoading(false);
        }
    };

    return (
        <Box sx={{ height: '100dvh' }}>
            <DetailsPageHeader pageTitle="Log In Using Single Sign-On" onBack={onBack} />
            <Box sx={{ p: 2.5 }}>
                <Typography variant="h2" sx={{ fontWeight: 'bold', mb: 1 }}>
                    Enter your email
                </Typography>

                <Typography variant="body2" component="p" sx={{ mb: 4 }}>
                    Use your work email to continue.
                </Typography>

                <form onSubmit={handleSubmit(onSubmit)}>
                    <Stack sx={{ gap: 2 }}>
                        <Stack sx={{ gap: 0.5 }}>
                            <FormLabel required htmlFor="email-input">
                                Email
                            </FormLabel>
                            <Controller
                                name="email"
                                control={control}
                                render={({ field: { onChange, value }, fieldState: { error } }) => (
                                    <TextField
                                        id="email-input"
                                        type="email"
                                        fullWidth
                                        variant="outlined"
                                        value={value}
                                        onChange={onChange}
                                        error={!!error}
                                        helperText={error?.message}
                                    />
                                )}
                                rules={{
                                    required: 'Email is required',
                                    pattern: {
                                        value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                                        message: 'The email is not in a correct format',
                                    },
                                }}
                            />
                        </Stack>

                        <Button
                            disableElevation
                            disabled={isLoading || !formState.isValid}
                            fullWidth
                            loading={isLoading}
                            type="submit"
                            variant="contained"
                        >
                            Continue
                        </Button>

                        <Typography variant="body2" sx={{ color: 'grey.900' }}>
                            We&apos;ll redirect you to your organization&apos;s sign-in page.
                        </Typography>
                    </Stack>
                </form>
            </Box>
        </Box>
    );
}
